import { apiConfig } from './authConfig';

//Contains a reference for getting a token, and user access
export interface IUserConfigPackage {
	entityTypes: EntityTypes;
	currentUserId: string;
	tenantUrl: string;
	tenantId: string;
	isAdmin: boolean;
	outlookPreventAttachments:boolean;
	getToken: () => Promise<string>;
}

export enum AccessLevel {
	None,
	Read,
	Manage,
}

export enum EntityType {
	None,
	EntityTypeDefinition,
	FilingLog,
	DocumentSearchManagedProperty,
	DocumentSearchStoredSearch,
	DocumentSearchScope,
	FormDefinition,
	Pin,
	ProvisionProfile,
	Tenant,
	TemplateTrigger,
	ContentMetadataField,
	AIPropertyDefinition,
	TriggerAction,
	Trigger
}

export class EntityTypes {
	public entityTypes: IEntityTypeUserInfo[];

	constructor(entityTypes: IEntityTypeUserInfo[]) {
		this.entityTypes = entityTypes;
	}

	public getTypeId(entityType: EntityType | string): string {
		let config = this.getConfig(entityType);
		return config?.id || '';
	}

	public isLoaded(): boolean {
		return this.entityTypes.length > 0;
	}

	public getConfig(
		entityType: string | EntityType,
	): IEntityTypeUserInfo | undefined {
		if (entityType) {
			return this.entityTypes.find(
				(e) =>
					e.type === Number(entityType) ||
					e.id === entityType /* generic types use ID rather than type */,
			);
		}
	}

	public getUserAccessibleEntityTypes(customOnly: boolean) {
		var accessible = this.entityTypes.filter(
			(e) => e.accessLevel !== AccessLevel.None,
		);
		if (customOnly) {
			return accessible.filter((e) => e.type === EntityType.None);
		} else {
			return accessible;
		}
	}

	public isVisible(entityType: string): boolean {
		return this.getAccess(entityType) !== AccessLevel.None;
	}

	public getAccess(entityType: string): AccessLevel {
		var config = this.getConfig(entityType);
		if (config) {
			return config.accessLevel;
		} else {
			return AccessLevel.Manage;
		}
	}
}


export enum TaskServiceType {
	None = 0,
	SP = 1,
	ToDo = 2,
	Planner = 3,
	OutlookFlaggedMails = 4,
	OutlookDrafts = 5,
	SQL = 6,
}

export enum TaskStoredQueryType {
	Assigned,
	All,
	AllManaged,
}
export interface IEmailCategoryInfo {
	name: string;
	color: number; //Office.MailboxEnums.CategoryColor;
}

interface IGetTasksRequest {
	ProjectId: string;
	PageLink: string;
	Filters: ITaskRequestFilterField[];
	SortField: string;
	SortAsc: boolean;
	SearchText?: string;
}

export interface IFilingResult {
	location: string;
	error: string;
	entity?:IGenericEntity; //Useful when filing resulted in a new entity being created
}

export interface IDownloadFileResult {
	name: string;
	file: ArrayBuffer;
}

export enum TopicIOD {
	Unset,
	Information,
	Orientatation,
	Discussion,
}

export interface IGenericEntity {
	Id: string;
	DisplayTitle: string;
	ServerRelativeUrl: string;
	NativeMetadataUrl: string;
	IsProvisioning: boolean;
}

export interface ITokenInformation {
	accountUserId: string;
	isAccountConfigured: boolean;
	token: string;
}

export interface IFilingUser {
	id: string;
	email: string;
	displayName: string;
}

export interface IFilingLogItem {
	logEntry: IFilingLog;
	category: IEmailCategoryInfo;
}

export interface IFilingInfoResult {
	log: IFilingLogItem[];
	users: IFilingUser[]; //Relevant users from the filing log
}

export interface IFilingLog extends IGenericEntity {
	createdDate: Date;
	createdBy: string;

	modifiedDate: Date;
	modifiedBy: string;

	entityId: string;
	entityType: string;
	filingUrl: string;
	filedUrl: string;
	filedLocationDisplayName: string;
	isAttachment: boolean;
	internetMessageId: string;
	conversationId: string;
	messageMailboxFolderName: string;

	category: IEmailCategoryInfo;
}

export interface IEntityTypeUserInfo {
	id: string;
	type: EntityType;
	isInternal: boolean;
	singularName: string;
	pluralName: string;
	sharePointListUrl: string;
	sharePointListNewFormUrl: string;
	sharePointTemplateDocumentsLibraryUrl: string;
	sharePointListId:string;
	accessLevel: AccessLevel;

	outlookEnableCreateNewOption: boolean;
	outlookEnableViewAllOption: boolean;
}

export enum ProvisionType {
	Site,
	Library,
	Folder,
	Team,
	TeamChannel,
	NoWorkspace //Use item attachments only
}

export enum LogLevel {
	Trace,
	Debug,
	Information,
	Warning,
	Error,
	Critical,
	None
}


export interface IPublishedLog {
	LogLevel: LogLevel;
	EventId: number;
	Message: string;
	UserId: string;
	DateTime: string;
	TenantId: string;
	EntityId: string;
	EntityTypeId: string;
}

export enum TriggerActionType {
	SetField,
	SendEmail
}

export interface ITriggerAction extends IGenericEntity {
	ActionType:TriggerActionType;
	ActionArgs:string;
}
export enum TriggerEvaluationPoint {
	BeforeEval,
	AfterEval
}

export interface ITrigger extends IGenericEntity {
	TriggerExpression:string;
	TriggerActionIds: string[];
	TriggerEvaluationPoint: TriggerEvaluationPoint;
}

export interface ITemplateTrigger extends IGenericEntity {
	TemplateUrl:string;
	CachedTemplateFile: string;
	TriggerExpression:string;
	TemplateProvisionIncludeSharePointIfTeams:boolean;
	TemplateProvisionIncludeFiles:boolean;
	TemplateProvisionIncludeListItems:boolean;
	OnCompletionTriggerActionIds: string[];
	TemplateApprovalListItemId:string;
}

export interface IAIPropertyDefinition extends IGenericEntity {
	FieldName:string;
	Prompt:string;
	WorkspacePath:string;
	Expression:string;
}

export interface IContentMetadataField extends IGenericEntity {
	ValueFromTokenizedValue:string;
	CreateFieldIfMissing:boolean;
	CreateFieldCustomFieldType:FieldType;
	DestinationFieldInternalName:string;
	CreateFieldDisplayName:string;
}

export enum SharingCapabilities {
	Disabled,
	ExternalUserSharingOnly,
	ExternalUserAndGuestSharing,
	ExistingExternalUserSharingOnly
}

export enum SecurityMode {
	None,
	OneOff,
	SyncFromMetadata
}

export interface IProvisionProfile extends IGenericEntity {
	Name: string;
	ProvisionType: ProvisionType;
	SiteTemplate:string;
	WorkspaceTitle: string;
	WorkspacePath: string;
	WorkspacePathComponent: string;
	SecurityMode: SecurityMode;
	PrivacyExpression:string;
	MetadataPrivacyExpression:string;
	Expression:string;
	CreateWorkspaceExpression:string;
	SecurityOwnerFields: string[];
	SecurityMemberFields: string[];
	SecurityVisitorFields: string[];
	SecurityOwners: string[];
	SecurityMembers: string[];
	SecurityVisitors: string[];
	FavouritesPeople: string[];
	MetadataFields: string[];
	TemplateTriggerIds: string[];
	TriggerIds: string[];
	AIPropertyIds:string[];
	ContentTypeIds:string[];
	FavouritesPeopleFields: string[];
	EnableOneDriveSync: boolean;
	OneDriveSyncPath: string;
	SiteSharingCapabilities: SharingCapabilities;
	MoveAttachmentsToWorkspace: boolean;
	MoveAttachmentsToWorkspacePath: string;
	SensitivityLabelExpression: string;
	MailNickname: string;
	Description: string;
	MetadataSecurityMode: SecurityMode;
	MetadataSecurityOwnerFields: string[];
	MetadataSecurityMemberFields: string[];
	MetadataSecurityVisitorFields: string[];
	MetadataSecurityOwners: string[];
	MetadataSecurityMembers: string[];
	MetadataSecurityVisitors: string[];
}

export interface IEntityTypeDefinitionProps {
	Id:string;
	SingularName: string;
	PluralName: string;
	Type: EntityType;
	SharePointListUrl: string;
	SharePointTemplateApprovals:boolean;
	SharePointTemplateApprovalsListUrl:string;
	SharePointStorageUrlField: string;
	SharePointStorageUrlPattern: string;
	SharePointTemplateDocumentsLibraryUrl:string;
	EnableWorkspaceSync: boolean;
	ProvisionProfiles: string[];
	OutlookFilingTagTemplate:string;
	OutlookIncludeInSearchResults:boolean;
	OutlookEnableCreateNewOption:boolean;
	OutlookEnableViewAllOption:boolean;
}

export interface IEntityTypeDefinition extends IGenericEntity, IEntityTypeDefinitionProps {
	
}

export interface IEntityQueryResult<IGenericEntity> {
	nextPageLink: string;
	items: IGenericEntity[];
}

export interface IEntityQueryRequest {
	parentId: string;
	searchText: string;
	ids: string[];
	queryTypes: number[];
	pageLink: string;
	filterFields: ITaskRequestFilterField[];
	sortField: string;
	sortAsc: boolean;
	pageSize: number;
	modifiedSince: string;
	viewId: string;
	timeTravel: string;
}

export interface ILegalEntity extends IGenericEntity {
	Name: string;
	CommercialName: string;
	StatutoryAddress: string;
	Street: string;

	StreetNumber: string;

	ZipCode: string;
	City: string;
	Country: string;

	LegalKind: string;
	InternalEntity: boolean;
	IsWorksCouncil: boolean;
	EstablishingDate: Date;
	TradeRegisterNumber: string;
	ResolutionDate: Date | null;
	MinisterialNumber: string;
	VATNumber: string;
	DateInGroup: Date;
	StatusInGroup: string;
	Responsible: string[];

	ActiveProjectId: string;
}

export interface ILegalEntityKindOfShare extends IGenericEntity {
	EntityId: string;
	LegalEntityName: string;
	Name: string;
	VotingRights: number;
	NominalValue: number;
	Currency: string;
}

export interface ILegalEntityRegisteredShare extends IGenericEntity {
	EntityId: string;
	LegalEntityName: string;
	KindOfShareId: string;
	KindOfShareName: string;
	KindOfShareVotingRights: number;
	KindOfShareCurrency: string;
	KindOfShareNominalValue: number;

	NumberOfShares: number;
	DateOfIssue: Date;
}

export interface ILegalEntityNaturalPerson extends IGenericEntity {
	FirstName: string;
	MiddleName: string;
	LastName: string;
	Initials: string;
	Prefix: string;
	Gender: string;
	BirthDate: Date;
	Street: string;
	StreetNumber: string;
	Apartment: string;
	City: string;
	ZipCode: string;
	Country: string;
	Nationality: string;
}

export interface IShareholding {
	OwnerId: string;
	OwnedId: string;
	PercentageOwnership: number;
}

export interface ILegalEntityOwnershipResult {
	Entities: ILegalEntity[];
	Persons: ILegalEntityNaturalPerson[];
	Shareholdings: IShareholding[];
}

export interface IChartRequest {
	EntityIds: string[];
	ShowOwnership: boolean;
}

export interface IEntitySecurityGroup extends IGenericEntity {
	userOrGroupIds: string[];
	accessLevel: AccessLevel;
	entityType: EntityType;
}

export enum RequestResponseType {
	JSON,
	Text,
	ArrayBuffer,
	None,
	Boolean,
}

export interface IEntityMatch {
	id: string;
	displayName: string;
	documents: IEntityContentItem[];
}

export interface IEntityV2QueryResultWithType {
    entityType:string;
    entities: IEntityQueryResult<IGenericEntity>;
}

export interface ISharePointSiteResult {
    webUrl: string;
    displayName: string;
}

export interface IGlobalLocations {
    entities: IEntityV2QueryResultWithType[];
    sites: ISharePointSiteResult[];
	any:boolean;
}

export interface ITeamSearchResult {
	displayName: string;
	id:string;
}

export interface ISiteAndTeamSearchResult {
	sites: ISharePointSiteResult[];
	teams: ITeamSearchResult[];
}

export interface IDocumentSearchRequest {
	searchText: string;
	searchScopeId: string;
	sortBy: string;
	sortAsc: boolean;
	includeOneDriveResult:boolean;
	scopeToPath: string;
}

//There will be extra properties on this object according to the supplied columns, but these define the standard result properties
export interface IDocumentSearchResultItem extends INavigableItem {
	name: string;
	webUrl: string;
	FileType: string;
	LastModifiedTime: string; //ISO8601 format
	Size: number; //number in bytes
	UIVersionStringOWSTEXT: string;
}

export interface IDocumentSearchResult {
	items: IDocumentSearchResultItem[];
	columns: IEntityColumn[];
}

export interface IFilingRequestBase {
	emailId: string;
	attachmentId: string;
	conversationId: string;
	internetMessageId: string;
	url: string;
	entityId: string;
	entityType: string;
	createNewEntity:boolean;
	senderEmail:string;
	fileName:string;
}

export interface IEmailFilingRequest extends IFilingRequestBase {
	
}

export interface ILocation {
	url: string;
	entityId: string;
	entityType: string;
}

export interface ISiteTemplate {
	name:string;
	title: string;
}

export interface IMemberTenantInfo {
	id: string;
	domain:string;
}

export interface ICurrentUserInfo {
	name:string;
	givenName:string;
	photoUrl:string;
	userId:string;
	currentTenantId:string;
	homeTenantId:string;
	memberTenants: IMemberTenantInfo[];
}
export interface IPeoplePickerSearchResultEntityData {
	title:string;
	email:string;
	objectId:string;
}
export interface IPeoplePickerSearchResult {
	key:string;
	displayText:string;
	description:string;
	isResolved:boolean;
	entityData: IPeoplePickerSearchResultEntityData;

}

export default class API {
	protected accessToken: string;

	constructor(accessToken: string) {
		this.accessToken = accessToken;
	}

	public async agentQuestion(question: string): Promise<string> {
		return await this.sendRequest(
			'POST',
			`/AI/Ask`,
			{
				prompt: question
			}
		);
	}

	public async searchPeople(siteUrl: string, searchText: string[]): Promise<IPeoplePickerSearchResult[]> {
		return await this.sendRequest(
			'POST',
			`/SharePoint/PeoplePicker?siteUrl=${encodeURIComponent(siteUrl)}}`, {
				Queries: searchText
			}
		);
	}

	public async getListTokens(listUrl: string): Promise<IEntityToken[]> {
		return await this.sendRequest(
			'GET',
			`/SharePoint/ListTokens?listUrl=${encodeURIComponent(listUrl)}`,
		);
	}

	public async getSiteTemplates(lcid:number, ctxUrl:string): Promise<ISiteTemplate[]> {
		return await this.sendRequest(
			'GET',
			`/SharePoint/SiteTemplates?lcid=${encodeURIComponent(lcid)}&ctxUrl=${encodeURIComponent(ctxUrl)}`,
		);
	}

	public async getListFields(listUrl: string): Promise<IEntityColumn[]> {
		return await this.sendRequest(
			'GET',
			`/SharePoint/ListColumns?listUrl=${encodeURIComponent(listUrl)}`,
		);
	}

	public async checkSPAuth(webUrl: string): Promise<ICheckAuthResult> {
		return await this.sendRequest(
			'GET',
			`/Account/CheckSPAuth?webUrl=${encodeURIComponent(webUrl)}`,
			undefined,
			false,
			false,
		);
	}

	public async hasScope(scope: string, isSharePoint: boolean, isApp:boolean): Promise<boolean> {
		return await this.sendRequest(
			'GET',
			`/Account/HasScope?scope=${encodeURIComponent(
				scope,
			)}&isSharePoint=${isSharePoint}&isApp=${isApp}`,
			undefined,
			false,
			false,
			RequestResponseType.Boolean,
		);
	}

	public async getCurrentUserInfo(): Promise<ICurrentUserInfo> {
		return await this.sendRequest('GET', `/Account/CurrentUserInfo`, undefined, false, false);
	}

	public async userPing(): Promise<void> {
		return await this.sendRequest(
			'GET',
			`/Account/UserPing`,
			undefined,
			false,
			false,
			RequestResponseType.None,
		);
	}

	public async clearTokens(): Promise<boolean> {
		return await this.sendRequest(
			'GET',
			`/Account/ClearTokens`,
			undefined,
			false,
			false,
			RequestResponseType.Boolean,
		);
	}

	public async getSettings(): Promise<ISettings> {
		return await this.sendRequest('GET', `/Settings`);
	}
	
	public async setUserEmailNotifications(enable: boolean): Promise<ISettings> {
		return await this.sendRequest(
			'GET',
			`/Settings/SetUserEmailNotifications?emailNotificationsEnabled=${
				enable ? 'true' : 'false'
			}`,
		);
	}

	public async setBackgroundMonitor(enable: boolean): Promise<ISettings> {
		return await this.sendRequest(
			'GET',
			`/Settings/SetBackgroundMonitor?enable=${enable ? 'true' : 'false'}`,
		);
	}

	public async setIsLive(projectId: string, isLive: boolean): Promise<void> {
		return await this.sendRequest(
			'POST',
			`/Project/SetIsLive?id=${projectId}&isLive=${isLive}`,
		);
	}

	public async setServiceAccountMode(useAppIdentity: boolean, userId:string): Promise<void> {
		return await this.sendRequest('GET', `/Settings/SetServiceAccountMode?useAppIdentity=${useAppIdentity}&serviceAccountUserId=${encodeURIComponent(userId)}`, undefined, false, false, RequestResponseType.None);
	}

	public async getTokenInformation(userId:string): Promise<ITokenInformation> {
		return await this.sendRequest('GET', `/Settings/GetTokenInformation?userId=${encodeURIComponent(userId)}`);
	}

	public async setEmailAdminOptions(enabled:boolean, userId:string, deleteOnFile:boolean, outlookPreventAttachments:boolean): Promise<void> {
		return await this.sendRequest('GET', `/Settings/SetEmailAdminOptions?enabled=${enabled || false}&userId=${encodeURIComponent(userId || '')}&deleteOnFile=${deleteOnFile || false}&outlookPreventAttachments=${outlookPreventAttachments || false}`, undefined, false, false, RequestResponseType.None);
	}

	public async addAdmin(id: string): Promise<void> {
		return await this.sendRequest('GET', `/Settings/AddAdmin?id=${id}`);
	}

	public async removeAdmin(id: string): Promise<void> {
		return await this.sendRequest('GET', `/Settings/RemoveAdmin?id=${id}`);
	}

	public static setCustomFieldValuesForAPI(customFields: ICustomFieldValue[]) {
		if (customFields) {
			customFields.forEach((f) => {
				f.internalValue = f.internalValue || '';
			});
		}
	}

	public async createCustomField(
		newField: ICreateCustomFieldRequest,
	): Promise<string> {
		return await this.sendRequest('POST', `/Project/CreateCustomField`, newField);
	}

	public async setProjectEndDate(id: string, newEndDate: string): Promise<void> {
		await this.sendRequest<void>(
			'GET',
			`/Project/SetEndDate?id=${id}&newEndDate=${newEndDate}`,
		);
	}

	public async setProjectCode(id: string, newCode: string): Promise<void> {
		await this.sendRequest<void>(
			'GET',
			`/Project/SetCode?id=${id}&newCode=${newCode}`,
		);
	}

	public async renameProject(id: string, newName: string): Promise<void> {
		await this.sendRequest<void>(
			'GET',
			`/Project/Rename?id=${id}&newName=${newName}`,
		);
	}

	public async getAllProjects(): Promise<IProject[]> {
		var projects = await this.sendRequest<IProject[]>('GET', `/Project/List`);

		this.preprocessProjects(projects);

		return projects;
	}

	private preprocessProjects(projects: IProject[]) {
		projects.forEach((project) => {
			project.targetEndDate = new Date(project.targetEndDate);
			project.startDate = new Date(project.startDate);
		});
	}

	public async getProject(id: string): Promise<IProject> {
		var project = await this.sendRequest<IProject>('GET', `/Project/?id=${id}`);

		if (project) {
			this.preprocessProjects([project]);
		}

		return project;
	}

	public async getProjectIdByCode(code: string): Promise<string> {
		var project = await this.sendRequest<IProject>(
			'GET',
			`/Project/GetIdByCode?code=${code}`,
		);
		return project?.Id;
	}

	public async unsubscribe(id: string): Promise<void> {
		return await this.sendRequest('GET', `/Project/Unsubscribe?id=${id}`);
	}

	public async subscribe(id: string): Promise<void> {
		return await this.sendRequest('GET', `/Project/Subscribe?id=${id}`);
	}

	public async getTasks(
		projectId: string | null,
		queryType: TaskStoredQueryType,
		pageLink: string,
		filters: ITaskRequestFilterField[],
		sortField: string,
		sortAsc: boolean,
		searchText?: string,
	): Promise<IProjectTasksResult> {
		if (!projectId) projectId = ''; //If it's null, it would be converted to "null" but we want to send empty string.

		if (queryType === TaskStoredQueryType.All && !projectId)
			throw new Error(
				`You can't get all tasks for all projects. Specify assignedOnly, or a project ID, or both.`,
			);

		let endpoint = 'All';

		if (queryType === TaskStoredQueryType.AllManaged) endpoint = 'Managed';
		else if (queryType === TaskStoredQueryType.Assigned) endpoint = 'Assigned';

		// Initialize the payload with mandatory properties.
		let payload: IGetTasksRequest = {
			ProjectId: projectId,
			PageLink: pageLink,
			Filters: filters,
			SortField: sortField,
			SortAsc: sortAsc,
		};

		// Conditionally add searchText if it has a value.
		if (searchText && searchText.trim() !== '') {
			payload.SearchText = searchText;
		}

		let response = await this.sendRequest<IProjectTasksResult>(
			'POST',
			`/Task/${endpoint}`,
			payload,
		);

		if (response) {
			//The dates come back as strings....
			response.tasks.forEach((task) => {
				this.processTask(task);
			});
		}

		return response;
	}

	private processTask(task: ITask) {
		if (task) {
			if (task.enddate) task.enddate = new Date(task.enddate);

			if (task.startdate) task.startdate = new Date(task.startdate);

			if (task.startdate) task.startdate = new Date(task.startdate);

			task.resources = task.resources || [];

			task.type = task.type || 'task';
		}
	}

	public async updateTasks(
		projectId: string,
		projectType: TaskServiceType,
		updateTasksObj: ITaskUpdate[],
	): Promise<void> {
		return await this.sendRequest('POST', `/Task/UpdateTasks`, {
			Project: {
				Id: projectId,
				Type: projectType,
			},
			Tasks: updateTasksObj,
		});
	}

	public async updateProjectCustomProperties(
		projectId: string,
		custom: ICustomFieldValue[],
	) {
		var customPropVals = custom
			.map((c) => {
				return {
					Name: c.internalName,
					Value: c.internalValue,
				};
			})
			.filter((c) => c.Value !== undefined && c.Value !== null);

		return await this.sendRequest<string>('POST', `/Project/CustomProperties`, {
			Id: projectId,
			Custom: customPropVals,
		});
	}

	public async createReminder(title: string, date: Date): Promise<void> {
		await this.createTask(
			{
				Id: '',
				type: 0,
			} as IProject,
			title,
			'',
			'',
			[],
			date,
			date,
		);
	}

	public async createTask(
		projectInfo: IProject,
		title: string,
		status: string,
		templateId: string,
		assignedTo: string[],
		dueDate: Date,
		startDate?: Date,
	): Promise<string> {
		let dueDateISO = dueDate.toISOString();
		let startDateISO = (startDate || new Date()).toISOString(); //Default to today start date

		return await this.sendRequest<string>('POST', `/Task/Create`, {
			Project: {
				Id: projectInfo.Id,
				Type: projectInfo.type,
			},
			Title: title,
			AssignedTo: assignedTo,
			TemplateId: templateId,
			StartDateISO: startDateISO,
			DueDateISO: dueDateISO,
			Description: '',
			AssociatedTaskId: '',
			Status: status,
			Custom: [],
		});
	}

	public async removeTask(projectInfo: IProject, taskId: string): Promise<void> {
		await this.sendRequest<void>('DELETE', `/Task`, {
			Project: {
				Id: projectInfo.Id,
				Type: projectInfo.type,
			},
			TaskId: taskId,
		});
	}

	public async getQueryData(
		queryName: string,
		projectId: string,
	): Promise<IChartSeries[]> {
		return await this.sendRequest<IChartSeries[]>(
			'GET',
			`/Project/QueryData?projectId=${projectId}&queryName=${queryName}`,
		);
	}

	public async getAvailableCustomFields(
		projectId: string,
	): Promise<ICustomFieldDefinition[]> {
		return await this.sendRequest<ICustomFieldDefinition[]>(
			'GET',
			`/Project/AvailableCustomFields?id=${projectId}`,
		);
	}

	public async entitySearchByPath(
		path: string
	): Promise<IEntityV2QueryResultWithType[]> {
		return await this.sendRequest(
			'GET',
			`/Search/ByPath?q=${encodeURIComponent(path)}`,
		);
	}

	public async combinedSearch(
		text: string
	): Promise<IGlobalLocations> {
		return await this.sendRequest(
			'GET',
			`/Search?q=${encodeURIComponent(text)}`,
		);
	}

	public async siteAndTeamSearch(
		text: string
	): Promise<ISiteAndTeamSearchResult> {
		return await this.sendRequest(
			'GET',
			`/Search/SitesAndTeams?q=${encodeURIComponent(text)}`,
		);
	}

	public async documentSearch(
		req: IDocumentSearchRequest,
	): Promise<IDocumentSearchResult> {
		return await this.sendRequest('POST', `/Search/Documents`, req);
	}

	public async question(fileId: string, question: string): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/AIQuestion?fileId=${encodeURIComponent(
				fileId,
			)}&question=${encodeURIComponent(question)}`,
			undefined,
			false,
			false,
			RequestResponseType.Text,
		);
	}
	
	public async pin(entityType: string, entityId: string, url: string) {
		return await this.sendRequest(
			'POST',
			`/Locations/Pin`,
			{
				EntityId: entityId || '',
				EntityType: entityType || null, //Null when it's a site etc
				Url: url || '',
			},
			false,
			false,
			RequestResponseType.None,
		);
	}

	public async unpin(entityType: string, entityId: string, url: string) {
		return await this.sendRequest(
			'POST',
			`/Locations/Unpin`,
			{
				EntityId: entityId || '',
				EntityType: entityType || null, //Null when it's a site etc
				Url: url || '',
			},
			false,
			false,
			RequestResponseType.None,
		);
	}

	public async pinned(): Promise<IGlobalLocations> {
		return await this.sendRequest('GET', `/Locations/Pinned`);
	}

	public async recent(): Promise<IEntityContentItem[]> {
		return await this.sendRequest('GET', `/Locations/Recent`);
	}

	public async suggested(
		relevantContacts: string[],
		subject: string,
		emailId: string,
		conversationId: string,
		internetMessageId: string,
	): Promise<IGlobalLocations> {
		return await this.sendRequest('POST', `/Locations/Suggested`, {
			relevantContact: relevantContacts[0],
			subject,
			emailId,
			conversationId,
			internetMessageId,
		});
	}

	public async deleteRelationship(
		projectId: string,
		relationshipId: string,
	): Promise<void> {
		return await this.sendRequest(
			'DELETE',
			`/Relationship/Delete?projectId=${projectId}&relationshipId=${relationshipId}`,
		);
	}

	public async updateRelationships(
		request: IUpdateRelationshipRequest,
	): Promise<void> {
		await this.sendRequest(
			'POST',
			`/Relationship/Update`,
			request,
			false,
			false,
			RequestResponseType.None,
		);
	}

	public async getRelationships(
		entityId: string,
	): Promise<IGetRelationshipsResult> {
		return await this.sendRequest<IGetRelationshipsResult>(
			'GET',
			`/Relationship?id=${entityId}`,
		);
	}

	public async previewChanges(
		request: IPreviewChangesRequest,
	): Promise<ITaskChangePreview> {
		request.dependencies.forEach((d) => {
			(d as any).id = '';
			(d as any).taskId = '';
		});

		let result = await this.sendRequest<ITaskChangePreview>(
			'POST',
			`/Relationship/PreviewChanges`,
			request,
		);

		result.items.forEach((item) => {
			item.newStartDate = new Date(item.newStartDate);
			item.newEndDate = new Date(item.newEndDate);
			item.oldStartDate = new Date(item.oldStartDate);
			item.oldEndDate = new Date(item.oldEndDate);
		});

		return result;
	}

	public async getLegalEntityOwnershipChart(
		entityIds: string[],
		includeOwnership: boolean,
		timeTravelDate: Date | null,
	): Promise<ILegalEntityOwnershipResult> {
		return await this.sendRequest<ILegalEntityOwnershipResult>(
			'POST',
			`/LegalEntityOwnership/Chart`,
			{
				EntityIds: entityIds,
				ShowOwnership: includeOwnership,
				TimeTravel: timeTravelDate ? timeTravelDate.toISOString() : '',
			} as IChartRequest,
		);
	}

	public async applyPreviewChanges(
		request: IApplyTaskChangePreviewRequest,
	): Promise<void> {
		return await this.sendRequest<void>(
			'POST',
			`/Relationship/ApplyPreviewChanges`,
			request,
		);
	}

	public async processPaymentToken(
		token: string,
	): Promise<ISubscription | IPreexistingSubscription> {
		return await this.sendRequest(
			'POST',
			'/TeamsSaasWebhook/ProcessToken',
			{
				token: token,
				product: 1, //Tasks for teams
			},
			true,
		);
	}

	public async setAccessGroups(request: IEntitySecurityGroup): Promise<void> {
		await this.sendRequest(
			'POST',
			'/EntitySecurityGroup/SetAccessGroups',
			request,
		);
	}

	public async getAllConfiguredAccessLevels(): Promise<IEntitySecurityGroup[]> {
		return await this.sendRequest<IEntitySecurityGroup[]>(
			'GET',
			`/EntitySecurityGroup/GetAllConfiguredAccessLevels`,
		);
	}

	public async getFilingInfo(
		internetMessageId: string
	): Promise<IFilingInfoResult> {
		let result = await this.sendRequest<IFilingInfoResult>(
			'GET',
			`/EmailFiling?internetMessageId=${encodeURIComponent(internetMessageId)}`,
		);

		result.log.forEach((logItem) => {
			logItem.logEntry.createdDate = new Date(logItem.logEntry.createdDate);
			logItem.logEntry.modifiedDate = new Date(logItem.logEntry.modifiedDate);
		});

		return result;
	}

	public async setMessageFilingOptions(
		conversationId: string,
		id: string,
		c: boolean,
	): Promise<void> {
		await this.sendRequest(
			'POST',
			`/EmailFiling/Options`,
			{
				conversationId: conversationId,
				messageId: id
			},
			false,
			false,
			RequestResponseType.None,
		);
	}

	public async createTag(
		location:ILocation
	): Promise<string> {
		return await this.sendRequest<string>(
			'POST',
			`/EmailFiling/Tag`,
			location,
			false,
			false,
			RequestResponseType.Text,
		);
	}

	public async tempAuthToken(): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/AuthToken`,
			undefined,
			false,
			false,
			RequestResponseType.Text,
		);
	}

	public async getLocationLink(spFileUrl: string): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/LocationUrl?documentUrlOrId=${encodeURIComponent(spFileUrl)}`,
			undefined,
			false,
			false,
			RequestResponseType.Text,
		);
	}

	public async getBrowseLocation(locationUrl: string): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/BrowseLocation?url=${encodeURIComponent(locationUrl)}`,
			undefined,
			false,
			false,
			RequestResponseType.Text,
		);
	}

	public async getEditLink(spFileUrl: string): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/EditUrl?documentUrlOrId=${encodeURIComponent(spFileUrl)}`,
			undefined,
			false,
			false,
			RequestResponseType.Text,
		);
	}

	public async prepareDownloadLink(spFileUrl: string): Promise<string> {
		let authToken = await this.tempAuthToken();
		return `${apiConfig.uri}/Content/Download?fileWebUrl=${encodeURIComponent(
			spFileUrl,
		)}&authtoken=${authToken}`;
	}

	//Given the ID or URL of a file, converts it to PDF and stores it and returns the new url
	public async convertToPDF(fileIdOrUrl: string): Promise<string> {
		return await this.sendRequest<string>(
			'GET',
			`/Content/ConvertToPDF?fileIdOrUrl=${encodeURIComponent(fileIdOrUrl)}`,
			undefined, false, false, RequestResponseType.Text
		);
	}

	//Given the ID or URL of a file, this will get a PDF rendering, img, etc
	public async webPreview(fileIdOrUrl: string): Promise<IDownloadFileResult> {
		return await this.sendRequest<IDownloadFileResult>(
			'GET',
			`/Content/WebPreview?documentUrlOrId=${encodeURIComponent(fileIdOrUrl)}`,
			undefined,
			false,
			false,
			RequestResponseType.ArrayBuffer,
		);
	}

	//Given the URL of a file, this will get a graph URL for a preview
	public async webPreviewUrl(fileUrl: string): Promise<string> {
		let authToken = await this.tempAuthToken();
		return `${
			apiConfig.uri
		}/Content/WebPreviewHTML?documentUrlOrId=${encodeURIComponent(
			fileUrl,
		)}&authtoken=${authToken}`;
	}

	public async createTemplate(name: string, sourceURL: string, includeFiles:boolean, includeListItems:boolean, lists:string[]): Promise<void> {
		return await this.sendRequest(
			'POST',
			'/Templates/Create',
			{
				name: name,
				sourceURL: sourceURL,
				includeFiles: includeFiles,
				includeListItems: includeListItems,
				listNames: lists
			},
			undefined,
			undefined,
			RequestResponseType.None,
		);
	}
	
	public async getContentHubTypes(ctxUrl:string): Promise<IContentHubType[]> {
		return await this.sendRequest<IContentHubType[]>(
			'GET', '/SharePoint/ContentHubTypes?ctxUrl=' + encodeURIComponent(ctxUrl));
	}
	
	getSensitivityLabels(): Promise<ISensitivityLabel[]> {
		return this.sendRequest<ISensitivityLabel[]>('GET', '/SharePoint/SensitivityLabels');
	}


	public async getTenantInfo(tenantDomain:string): Promise<IMemberTenantInfo> {
		return await this.sendRequest<IMemberTenantInfo>(
			'GET', '/Account/GetTenantInfo?domain=' + encodeURIComponent(tenantDomain));
	}
	
	public async syncTemplate(templateId: string): Promise<void> {
		return await this.sendRequest(
			'POST',
			'/Templates/Sync',
			{
				templateId: templateId,
			},
			undefined,
			undefined,
			RequestResponseType.None,
		);
	}

	public async documentsByFolder(
		url: string,
		flatFileView: boolean,
		searchText: string = '',
	): Promise<IEntityDocumentResult> {
		return await this.sendRequest<IEntityDocumentResult>(
			'GET',
			`/Content/FolderContents?url=${encodeURIComponent(
				url,
			)}&flatFileView=${flatFileView}&searchQuery=${encodeURIComponent(
				searchText,
			)}`,
		);
	}

	protected async sendRequest<T>(
		method: string,
		path: string,
		body: object | undefined = undefined,
		isUsageApi: boolean = false,
		catchException: boolean = true,
		responseType: RequestResponseType = RequestResponseType.JSON,
	): Promise<T> {
		const headers = new Headers();
		const bearer = `Bearer ${this.accessToken}`;

		headers.append('Authorization', bearer);

		if (body) {
			headers.append('Accept', 'application/json');
			headers.append('Content-Type', 'application/json');
		}

		const options: RequestInit = {
			method: method,
			headers: headers
		};

		if (body) {
			options.body = JSON.stringify(body);
		}

		let uri = isUsageApi ? apiConfig.usageUri : apiConfig.uri;

		let fetchResult = await fetch(uri + path, options);
		let result: any;

		if (method.toLowerCase() === 'delete') {
			responseType = RequestResponseType.None;
		}

		if (responseType === RequestResponseType.JSON)
			result = await fetchResult.json();
		else if (responseType === RequestResponseType.Text) {
			result = await fetchResult.text();

			//If it's a POST, and the result is in quotes, remove them
			if (method.toLowerCase() === 'post' && typeof result === 'string') {
				result = (result || "").replace(/^"(.*)"$/, '$1');
			}

		}
		else if (responseType === RequestResponseType.Boolean)
			result = (await fetchResult.text()).toLowerCase() === 'true';
		else if (responseType === RequestResponseType.ArrayBuffer) {
			let fileName = 'Attachment';
			if (fetchResult.headers) {
				let part = (fetchResult.headers.get('Content-Disposition') || '').split(
					'filename=',
				)[1];
				if (part) {
					fileName = (part.split(';')[0] || '').replace(/^"(.*)"$/, '$1');
				}
			}

			result = await fetchResult.arrayBuffer();

			return {
				name: fileName,
				file: result,
			} as any;
		}

		if (catchException) {
			//result = result.catch((error:any) => console.log(error));
		}

		//Check if it's an authentication required result
		return await result;
	}
}

export interface IAccountInfo {
	message: string;
	banner: string;
	username: string;
	email: string;
	subscription: ISubscription;
}

export interface ISubscription {
	id: string;
	tenantId: string;
	ownerId: string;
	startDate: Date;
	endDate: Date;
	offerId: string;
	planId: string;
	purchaserEmail: string;
	isActive: boolean;
}

export interface IPreexistingSubscription {
	preexistingSubscriptionId: string;
	planId: string;
	activatedDate: Date;
}

export interface ICheckAuthResult {
	launchAuthenticationURL: boolean;
	webUrl: string;
}

export interface ICreateProjectResult {
	id: string;
	error: string;
}

export enum TaskStatusType {
	Unset,
	NotStarted,
	InProgress,
	Done,
}

export interface ITaskStatus {
	name: string; //need to add color to this
	color: string;
	type: TaskStatusType;
}

export interface IEntityColumn {
	id: string;
	multi: boolean;
	lookupType: string;
	showInDefaultView: boolean;
	isHidden:boolean;
	displayName: string;
	fieldType: FieldType;
	displayWidth: number;
	choices: string[];
}

export interface ICustomFieldDisplayOptions extends ICustomFieldDefinition {
	displayWidth: number;
}

export interface ICustomFieldDefinition extends IGenericEntity {
	aiPrompt?: string;
	displayName: string;
	fieldType: FieldType;
	choices: string[];
}

export interface ICreateCustomFieldRequest {
	displayName: string;
	fieldType: FieldType;
	choices: string[];
}

export interface ICustomFieldValue {
	internalName: string;
	internalValue: string;
}

export interface IFieldOption {
	display_value: string;
	internal_value: string;
}

export interface INavigableItem {
	name: string;
	webUrl: string;
}

export interface IEntityContentItem extends INavigableItem {
	id: string;
	name: string;
	icon: string;
	webUrl: string;
	previewImageUrl: string;
	modified: Date;
	lastAccessed:Date;
	modifiedBy: string;
	modifiedByName: string;
}

export interface IEntityDocumentResult {
	documents: IEntityContentItem[];
	folders: IEntityContentItem[];
	webUrl: string;
}

export enum FieldType {
	Invalid,
	Integer,
	Text,
	Note,
	DateTime,
	Counter,
	Choice,
	Lookup,
	Boolean,
	Number,
	Currency,
	Url,
	Computed,
	Threading,
	Guid,
	MultiChoice,
	GridChoice,
	Calculated,
	File,
	Attachments,
	User,
	Recurrence,
	CrossProjectLink,
	ModStat,
	Error,
	ContentTypeId,
	PageSeparator,
	ThreadIndex,
	WorkFlowStatus,
	AllDayEvent,
	WorkflowEventType,
	CustomFieldType,
}

export interface IProjectConfig {
	statuses: ITaskStatus[];
	customFieldDisplayOptions: ICustomFieldDisplayOptions[];
	taskDescriptionField: string;
	taskCodeField: string;
	taskStartDateField: string;
	taskDueDateField: string;
	taskProgressField: string;
	taskResourcesField: string;
	taskParentField: string;
	taskPredecessorsField: string;
	taskStatusField: string;
	taskTypeField: string;
	filterFieldName: string;
	filterFieldValue: string;

	taskTemplateIds: string[];
	defaultTaskTemplateId: string;
}

export interface IAccountTokenResult {
	access_token: string;
}

export interface ITemplate {
	id: string;
	name: string;
}

export enum RelationshipRuleStartEnd {
	Start,
	End,
}

export enum RelationshipRuleBeforeAfter {
	Before,
	After,
}

export enum LessMoreExact {
	Immediate,
	LessThan,
	MoreThan,
	Exactly,
}

export interface IRelationshipRule {
	id: string;
	relatedTaskStartOrEnd: RelationshipRuleStartEnd;
	lessMoreExact: LessMoreExact;
	days: number;
	beforeAfter: RelationshipRuleBeforeAfter;
	startOrEnd: RelationshipRuleStartEnd;
}

export interface IRelationship {
	id: string;
	taskId: string;
	relatedTaskId: string;
	relationshipRule: IRelationshipRule;
}

export interface IGetRelationshipsResult {
	relationships: IRelationship[];
	related: INewTask[];
}

export interface IApplyTaskChangePreviewRequestItem {
	taskId: string;
	isChanged: boolean;
	newStartDate: Date;
	newEndDate: Date;
}

export interface ITaskChangePreviewItem {
	ruleId: string;
	rule: IRelationshipRule;
	taskId: string;
	task: INewTask;
	triggeringTaskId: string;
	isChanged: boolean;
	newStartDate: Date;
	newEndDate: Date;
	oldStartDate: Date;
	oldEndDate: Date;
	days: number;
}

export interface ITaskChangePreview {
	items: ITaskChangePreviewItem[];
	error: string;
}

export interface IApplyTaskChangePreviewRequest {
	items: IApplyTaskChangePreviewRequestItem[];
}

export interface IUpdateProperty {
	name: string;
	value: any;
}

export interface ITaskUpdate {
	itemId: string;
	updates: IUpdateProperty[];
}

export interface IPreviewChangesRequest {
	update: ITaskUpdate;
	dependencies: IRelationship[]; //If supplied, will use these dependencies to generate a preview rather than the ones currently stored for the task.
}

export interface IUpdateRelationshipRequest {
	taskId: string;
	relationships: IRelationship[];
}

export interface IProject extends IGenericEntity {
	type: TaskServiceType;
	name: string;
	code: string;
	link: string;
	teamId: string;
	channelId: string;
	managerId: string;
	members: IPersonInfo[];
	listUrl: string;
	status: string;
	startDate: Date;
	targetEndDate: Date;
	daysUntilTargetEndDate: number;
	taskHoursRemaining: number;
	taskHoursTotal: number;
	progressPercentageBasedOnTime: number;
	progressPercentageBasedOnTasks: number;
	isSubscribed: boolean;
	isLive: boolean;
	statuses: ITaskStatus[];
	customFieldDisplayOptions: ICustomFieldDisplayOptions[];
	customFieldValues: ICustomFieldValue[];
	taskTemplateIds: string[];
	defaultTaskTemplateId: string;
	workspaceUrl: string;
	legalEntityId: string;
}

export interface INewProject extends IGenericEntity {
	name: string;
	managerId: string;
	status: string;
}

export interface ITodoTask {
	id: string;
	title: string;
	status: number;
	dueDate: Date;
	body: string;
}

export interface ITodoTasksPage extends ICheckAuthResult {
	pagingInfo: string;
	items: ITodoTask[];
}

export interface INewTask extends IGenericEntity {
	projectId: string;
	startDate: Date;
	endDate: Date;
	status: string;
	title: string;
	parent?: string;
	resources: string[];
	projectName: string;

	legalEntityIds: string[];

	relatedEntityType: string;
	relatedEntityId: string;
	relatedEntityFieldName: string;

	calendarEventId: string;
}

export interface ITask extends IGenericEntity {
	projectId: string;

	text: string;
	code: string;
	description: string;
	status: string;
	parent: string;
	progress: number;
	startdate: Date;
	completed_date: Date;
	enddate: Date;
	resources: string[];
	type: string;
	deeplink: string;
	template_id: string;

	//These are all ignored and should be removed
	assignments_sent: string[];
	calendar_event_id: string;
	custom_field_values: string[];
	modified_by: string;
}

export interface IProjectTasksResult {
	projectInfos: IProject[];
	nextPageLink: string;
	loadErrors: string[];
	tasks: ITask[];
}
export declare enum PersonaPresence {
    none = 0,
    offline = 1,
    online = 2,
    away = 3,
    dnd = 4,
    blocked = 5,
    busy = 6
}
export interface IPersonInfo {
	//AAD ID
	id: string | undefined;

	//SP field user id
	lookupId: number | undefined;

	title: string;
	initials: string;
	imageUrl: string;
	email: string;
	jobTitle: string;
	presence: PersonaPresence;
}

export interface ICreateProjectRequest {
	name: string;
	teamId: string;
	channelId: string;
	webUrl: string;
	managerId: string;
	templateId: string;
	templateStartDateISO: string;
	existingListConnectionInfo: IExistingListConnectionInfo;
	useSharePointTaskList: boolean;
}

export interface IExistingListConnectionInfo {
	listUrl: string;
	createMissingFields: boolean;
	titleField: string;
	statusField: string;
	assignedField: string;
	startDateField: string;
	endDateField: string;
	dependenciesField: string;
	parentField: string;
	descriptionField: string;
	codeField: string;
	progressField: string;
	filterFieldName: string;
	filterFieldValue: string;
}

export interface ISettings {
	administratorUserIds: string[];
	useAppIdentity: boolean;
	serviceAccountId:string;

	currentUserId: string;
	tenantId: string;
	tenantUrl: string;
	isAdmin: boolean;
	entityTypes: IEntityTypeUserInfo[];

	emailFilingUserId: string;
	emailFilingEnabled: boolean;
	emailFilingDeleteOnFile: boolean;
	outlookPreventAttachments: boolean;
}

export interface IContentHubColumn {
	name: string;
}

export interface ISensitivityLabel {
	id: string;
	name: string;
	applicableTo:string;
}

export interface IContentHubType {
	id:string;
	name:string;
	columns:IContentHubColumn[];
}

export interface IEntityAccessLevel {
	accessLevel: AccessLevel;
	entityType: EntityType;
}

export interface IChartSeries {
	name: string;
	dataPoints: IDataPoint[];
}

export interface IDataPoint {
	category: string;
	value: number;
}

export interface IListField {
	title: string;
	type: number;
	internalName: string;
}

export interface IListLoadResult {
	fields: IListField[];
	suggestedTitleField: string;
	suggestedStatusField: string;
	suggestedAssignedField: string;
	suggestedStartDateField: string;
	suggestedEndDateField: string;
	suggestedDependenciesField: string;
	suggestedParentField: string;
	suggestedCodeField: string;
	suggestedDescriptionField: string;
	suggestedProgressField: string;
	suggestedTaskTypeField: string;
}

//This maps to the server - see CreateTaskArgs. It's sent by the bot for the "create a task" extension
export interface ICreateTaskArgs {
	assignedTo: string[];
	title: string;
	projectInfo: IProject;
}

export enum TaskRequestFilterFieldComparator {
	Equals,
	NotEquals,
	Contains,
	GreaterThanEquals,
	LessThanEquals,
	DaysOffset,
}

export interface ITaskRequestFilterField {
	fieldName: string;
	fieldValue: string;
	comparator: TaskRequestFilterFieldComparator;
}

export interface ITaskTemplate {
	id: string;
	name: string;
	assignmentFieldDefinitionIds: string[];
}

export interface IVersionChangeProperty {
	internalName: string;
	before: string;
	after: string;
}

export interface IVersion {
	modifiedBy: string;
	modifiedDate: Date;
	changes: IVersionChangeProperty[];
}

export interface IVersionHistory {
	versions: IVersion[];
	nextPageLink: string;
}

export interface IGeneralTemplate extends IGenericEntity {
	Name: string;
	Type: string;
	Text: string;
}

export interface IDocumentSearchManagedProperty extends IGenericEntity {
	Title: string;
	InternalName: string;
}

export interface IDocumentSearchScope extends IGenericEntity {
	Title: string;
	Scope: string;
}

export interface IDocumentSearchStoredSearch extends IGenericEntity {
	Title: string;
	SearchScopeId: string;
	SortBy: string;
	SortAsc: boolean;
	SearchText: string;
	Fields: string[];
}

export interface IEntityDashboardConfigurationTemplate {
	tableDisplayColumnInternalNames: string[];
	filterFields: ITaskRequestFilterField[];
	sortField: string;
	sortAsc: boolean;
}

export interface IViewInfo {
	id: string;
	title: string;
	viewJson: string;
	config: IEntityDashboardConfigurationTemplate;
}

export interface IFormField {
	id: string;
	title: string;
	width: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
}

export interface IFormRow {
	fields: IFormField[];
}

export interface IFormTab {
	title: string;
	sections: IFormSection[];
}

export interface IFormSection {
	title: string;
	rows: IFormRow[];
}

export interface IFormDefinition {
	name: string;
	tabs: IFormTab[];
}

export interface IEntityToken {
	token: string;
	value: string;
	pathDescription: string;
}

export interface IEntityAPI<IGenericEntity> {
	getListFields(listUrl: string): Promise<IEntityColumn[]>;
	tokens(): Promise<IEntityToken[]>;
	upsert(request: IGenericEntity, attachmentIds: string[]): Promise<string>;
	upload(
		file: File,
		url?: string,
		onProgress?: (p: number) => void,
	): Promise<IFilingResult>;
	aiModelFromFile(fileId: string): Promise<IGenericEntity>;
	question(fileId: string, question: string): Promise<string>;
	item(id: string, timeTravelDate: Date | null): Promise<IGenericEntity | null>;
	delete(ids: string[]): Promise<void>;
	query(
		request: IEntityQueryRequest,
	): Promise<IEntityQueryResult<IGenericEntity>>;
	queryByField(
		fieldName: string,
		fieldValue: string,
	): Promise<IEntityQueryResult<IGenericEntity>>;
	filterOptions(
		fieldName: string,
		parentId?: string | undefined,
	): Promise<IFieldOption[]>;
	versionHistory(id: string, nextPageLink: string): Promise<IVersionHistory>;
	documents(id: string, flatFileView: boolean, searchQuery: string): Promise<IEntityDocumentResult>;
	documentsByFolder(
		url: string,
		flatFileView: boolean,
		searchText?: string,
	): Promise<IEntityDocumentResult>;
	form(): Promise<IFormDefinition>;
	columns(): Promise<IEntityColumn[]>;
	views(): Promise<IViewInfo[]>;
	updateView(view: IViewInfo): Promise<string>;
	deleteView(id: string): Promise<void>;
	pinnedIds(): Promise<string[]>;
	pin(entityId: string): Promise<void>;
	unpin(entityId: string): Promise<void>;
	
	addDocument(entityId:string, documentTemplateUrl:string, locationUrl:string): Promise<void>;
	documentTemplates(): Promise<IEntityDocumentResult>;

}

export class EntityAPI<TEntity extends IGenericEntity> extends API {
	
	private urlSubPath: string;

	private constructor(path: string, accessToken: string) {
		super(accessToken);
		this.urlSubPath = path;
	}

	public static fromPath(
		path: string,
		accessToken: string,
	): EntityAPI<IGenericEntity> {
		return new EntityAPI<IGenericEntity>(path, accessToken);
	}

	public static async fromEntityTypeOrId(
		typeOrId: string | EntityType,
		userConfig: IUserConfigPackage,
	): Promise<EntityAPI<IGenericEntity>> {
		if (((typeOrId as string) || '').length > 10) {
			let accessToken = await userConfig.getToken();
			return this.fromEntityTypeId(typeOrId as string, accessToken);
		} else {
			return await this.fromEntityType(typeOrId as EntityType, userConfig);
		}
	}

	public static fromEntityTypeId<TEntity extends IGenericEntity>(
		id: string,
		accessToken: string,
	): EntityAPI<TEntity> {
		return new EntityAPI<TEntity>('Entity/' + id, accessToken);
	}

	public static async fromEntityType<TEntity extends IGenericEntity>(
		entityType: EntityType,
		userConfig: IUserConfigPackage,
	): Promise<EntityAPI<TEntity>> {
		let matchingEntity = userConfig.entityTypes.getConfig(entityType);
		let token = await userConfig.getToken();
		if (matchingEntity) {
			return this.fromEntityInfo(matchingEntity, token);
		}

		throw Error('No matching entity found for type ' + entityType);
	}

	public static fromEntityInfo<TEntity extends IGenericEntity>(
		entityInfo: IEntityTypeUserInfo | undefined,
		accessToken: string,
	): EntityAPI<TEntity> {
		if(!entityInfo?.id) {
			throw Error('No entity type is available');
		}
		return this.fromEntityTypeId(entityInfo.id, accessToken);
	}

	public async tokens(): Promise<IEntityToken[]> {
		return await this.sendRequest('GET', `/${this.urlSubPath}/Tokens`);
	}

	public async views(): Promise<IViewInfo[]> {
		return await this.sendRequest('GET', `/${this.urlSubPath}/Views`);
	}

	public async updateView(view: IViewInfo): Promise<string> {
		return await this.sendRequest<string>(
			'POST',
			`/${this.urlSubPath}/View`,
			view,
		);
	}
	
	public getLastUserCreatedItemId() : Promise<string> {
		return this.sendRequest<string>('GET', `/${this.urlSubPath}/LastUserCreatedItemId`, undefined, false, false, RequestResponseType.Text);
	}

	public async provision(id:string, template:string): Promise<string> {
		return await this.sendRequest<string>(
			'POST',
			`/${this.urlSubPath}/Provision?id=${encodeURIComponent(id)}&template=${encodeURIComponent(template)}}`
		);
	}

	public async syncWorkspace(id:string): Promise<string> {
		return await this.sendRequest<string>(
			'POST',
			`/${this.urlSubPath}/SyncWorkspace?id=${encodeURIComponent(id)}`
		);
	}

	public async applyTemplate(itemId: string, templateUrl: string, includeSharePointIfTeams:boolean, includeFiles:boolean, includeListItems:boolean): Promise<void> {
		return await this.sendRequest(
			'POST',
			`/${this.urlSubPath}/ApplyTemplate?id=${encodeURIComponent(itemId)}&template=${encodeURIComponent(templateUrl)}&includeSharePointIfTeams=${includeSharePointIfTeams}&includeFiles=${includeFiles}&includeListItems=${includeListItems}`,
		);
	}

	public async deleteView(id: string): Promise<void> {
		await this.sendRequest<string>('DELETE', `/${this.urlSubPath}/View?id=${id}`);
	}

	
	public async pinnedIds() : Promise<string[]> {
		return await this.sendRequest<string[]>('GET', `/${this.urlSubPath}/Pinned`);
	}

	public async pin(entityId: string): Promise<void> {
		await this.sendRequest<void>('POST', `/${this.urlSubPath}/Pin?id=${encodeURIComponent(entityId)}`);
	}

	public async unpin(entityId: string): Promise<void> {
		await this.sendRequest<void>('POST', `/${this.urlSubPath}/Unpin?id=${encodeURIComponent(entityId)}`);
	}


	public async upsert(
		request: IGenericEntity,
		attachmentIds: string[],
	): Promise<string> {
		return await this.sendRequest('POST', `/${this.urlSubPath}`, {
			entity: request,
			attachmentIds: attachmentIds,
		});
	}

	public async addDocument(entityId:string, documentTemplateUrl:string, locationUrl:string): Promise<void> {
		return await this.sendRequest('POST', `/${this.urlSubPath}/AddDocument?id=${encodeURIComponent(entityId)}&templateDocumentUrl=${encodeURIComponent(documentTemplateUrl)}&destinationUrl=${encodeURIComponent(locationUrl)}`);
	}

	public async documentTemplates(): Promise<IEntityDocumentResult> {
		return await this.sendRequest<IEntityDocumentResult>('GET', `/${this.urlSubPath}/DocumentTemplates`);
	}

	public async delete(ids: string[]): Promise<void> {
		return await this.sendRequest('DELETE', `/${this.urlSubPath}`, ids);
	}

	public async query(
		request: IEntityQueryRequest,
	): Promise<IEntityQueryResult<TEntity>> {
		return await this.sendRequest('POST', `/${this.urlSubPath}/Query`, request);
	}

	public async all(): Promise<IEntityQueryResult<TEntity>> {
		return await this.query({
			parentId: '',
			searchText: '',
			ids: [],
			queryTypes: [],
			pageLink: '',
			filterFields: [],
			sortAsc: false,
			sortField: '',
			pageSize: 100,
			modifiedSince: '',
			viewId: '',
			timeTravel: '',
		});
	}

	public async queryByField(
		fieldName: string,
		fieldValue: string,
	): Promise<IEntityQueryResult<TEntity>> {
		return await this.query({
			parentId: '',
			searchText: '',
			ids: [],
			queryTypes: [],
			pageLink: '',
			filterFields: [
				{
					fieldName: fieldName,
					fieldValue: fieldValue,
					comparator: TaskRequestFilterFieldComparator.Equals,
				},
			],
			sortAsc: false,
			sortField: '',
			pageSize: 100,
			modifiedSince: '',
			viewId: '',
			timeTravel: '',
		});
	}

	public async columns(): Promise<IEntityColumn[]> {
		return await this.sendRequest<IEntityColumn[]>(
			'GET',
			`/${this.urlSubPath}/Columns`,
		);
	}
	
	public async form(): Promise<IFormDefinition> {
		return await this.sendRequest<IFormDefinition>(
			'GET',
			`/${this.urlSubPath}/Form`,
		);
	}

	public async filterOptions(
		fieldName: string,
		parentId?: string | undefined,
	): Promise<IFieldOption[]> {
		return await this.sendRequest<IFieldOption[]>(
			'GET',
			`/${this.urlSubPath}/FieldOptions?id=${parentId}&fieldName=${fieldName}`,
		);
	}

	public async documents(
		id: string,
		flatFileView: boolean,
		searchQuery: string = ''
	): Promise<IEntityDocumentResult> {
		return await this.sendRequest<IEntityDocumentResult>(
			'GET',
			`/${this.urlSubPath}/Documents?entityId=${id}&flatFileView=${flatFileView}&searchQuery=${encodeURIComponent(searchQuery)}`,
		);
	}

	public async items(
		ids: string[],
		timeTravelDate: Date | null,
	): Promise<TEntity[]> {
		var result = await this.query({
			parentId: '',
			searchText: '',
			ids: ids,
			queryTypes: [],
			pageLink: '',
			filterFields: [],
			sortAsc: false,
			sortField: '',
			pageSize: 1,
			modifiedSince: '',
			viewId: '',
			timeTravel: timeTravelDate ? timeTravelDate.toISOString() : '',
		});

		if (!result.items) {
			return [];
		}

		return result.items;
	}

	public async item(
		id: string,
		timeTravelDate: Date | null,
	): Promise<TEntity | null> {
		if (id === '00000000-0000-0000-0000-000000000000') {
			return null;
		}

		if (id === '') return null;

		var result = await this.query({
			parentId: '',
			searchText: '',
			ids: [id],
			queryTypes: [],
			pageLink: '',
			filterFields: [],
			sortAsc: false,
			sortField: '',
			pageSize: 1,
			modifiedSince: '',
			viewId: '',
			timeTravel: timeTravelDate ? timeTravelDate.toISOString() : '',
		});

		if (!result.items) {
			return null;
		}

		return result.items[0] || null;
	}

	public async versionHistory(
		id: string,
		nextPageLink: string,
	): Promise<IVersionHistory> {
		let result = await this.sendRequest<IVersionHistory>(
			'POST',
			`/${this.urlSubPath}/VersionHistory`,
			{
				id: id,
				pageLink: nextPageLink,
				pageSize: 200,
			},
		);

		result.versions.forEach((v) => {
			v.modifiedDate = new Date(v.modifiedDate);
		});

		return result;
	}

	//Upload a file and get an ID for it.
	//If the URL is blank, it still gets stored, but in the users' OneDrive.
	public async upload(
		file: File,
		url: string = '',
		onProgress?: (p: number) => void,
	): Promise<IFilingResult> {
		var data = new FormData();
		data.append('File', file);
		data.append('Url', url);
		return await this.doUpload(`/Content/upload`, file, data, onProgress);
	}

	//Upload a file and get an ID for it.
	public async saveAttachment(
		file: File,
		request: IFilingRequestBase,
		onProgress?: (p: number) => void,
	): Promise<IFilingResult> {
		var data = new FormData();
		data.append('File', file);
		data.append('Url', request.url);
		data.append('EmailId', request.emailId);
		data.append('EntityId', request.entityId);
		data.append('EntityType', request.entityType);
		data.append('ConversationId', request.conversationId);
		data.append('InternetMessageId', request.internetMessageId);
		data.append('SenderEmail', request.senderEmail);
		data.append('CreateNewEntity', request.createNewEntity ? 'true' : 'false');
		return await this.doUpload(`/EmailFiling/upload`, file, data, onProgress);
	}

	//Upload a file and get an ID for it.
	private async doUpload(
		endpoint: string,
		file: File,
		data: FormData,
		onProgress?: (p: number) => void,
	): Promise<IFilingResult> {
		return new Promise<IFilingResult>((result) => {
			var ajax = new XMLHttpRequest();
			ajax.upload.addEventListener(
				'progress',
				(e) => {
					if (onProgress) {
						var percent = Math.round((e.loaded / e.total) * 100);
						console.log(`uploading ${percent}%`);
						onProgress(percent);
					}
				},
				false,
			);

			ajax.addEventListener(
				'load',
				(e) => {
					let response = ((e?.target as any) || {}).responseText || '';
					result(JSON.parse(response));
				},
				false,
			);

			ajax.open('POST', apiConfig.uri + endpoint);
			ajax.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);
			ajax.send(data);
		});
	}

	public async saveEmailOrAttachmentById(request: IEmailFilingRequest): Promise<IFilingResult> {
		return await this.sendRequest<IFilingResult>('POST', `/EmailFiling`, request);
	}

	//Given the ID of a previously uploaded file, this will generate a model for it.
	public async aiModelFromFile(fileId: string): Promise<IGenericEntity> {
		return await this.sendRequest<IGenericEntity>(
			'GET',
			`/${this.urlSubPath}/AIModelFromFile?fileId=${encodeURIComponent(fileId)}`,
		);
	}

}
